import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";

import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import RequestAssignmentApi from "../../../../../infrastructure/api/requestAssignment";
import CustomerRequestLocationService from "../../../../../infrastructure/api/customerRequestLocation";
import EnhancedTable from "../../../../components/shared/table";
import {
  ASSIGNMENT_FILTER_OPTIONS_OPERATOR,
  assignmentOwnerColumn,
  canShowClaimAction,
  enrichAssignmentRows,
  getCurrentUserId,
} from "../../../../components/request/requestAssignmentDisplay";
import { useManagementAllRequestPagedList } from "../../../../components/request/useManagementAllRequestPagedList";
import AllRequestsCaseDrawer from "./AllRequestsCaseDrawer";
import {
  ALL_REQUESTS_LOAN_STATUS_FILTERS,
  ALL_REQUESTS_STATUS_FILTERS,
  buildLocationLookupMaps,
  enrichAllRequestRows,
  filterAllRequestsRows,
} from "./allRequestsPageUtils";
import PartnerReferralFilterSelect from "../shared/PartnerReferralFilterSelect";
import {
  buildProjectWorkspacePath,
  openProjectWorkspacePathInNewTab,
  WORKSPACE_LIST_VIEWS,
} from "../../shared/projectWorkspace/projectWorkspacePaths";
import {
  buildCheckNewRequestModifyPath,
  openCheckNewRequestModifyPath,
} from "../../shared/checkNewRequest/checkNewRequestPageUtils";
import {
  createDefaultDrillDownParams,
  drillDownParamsToSearchParams,
  parseAllRequestDrillDownParams,
  withPageReset,
} from "../../management/all-request/allRequestDrillDownUtils";
import {
  columnIdToSortBy,
  sortByToColumnId,
} from "../../management/all-request/allRequestPaginationUtils";

const PAGE_TITLE = "مرکز پیگیری درخواست‌ها";
const LIST_API = "SLN/SolarentOperator/AllRequests";
/** صفحه کوچک‌تر = پاسخ سریع‌تر (enricherهای سمت سرور روی هر ردیف هزینه دارند). */
const OPERATOR_PAGE_SIZE = 25;

const provinceOptionId = (p) => p?.id ?? p?.Id ?? p?.value ?? p?.Value ?? "";
const provinceOptionLabel = (p) =>
  p?.provinceName ??
  p?.ProvinceName ??
  p?.title ??
  p?.Title ??
  p?.label ??
  p?.Label ??
  "";
const cityOptionId = (c) => c?.id ?? c?.Id ?? c?.value ?? c?.Value ?? "";
const cityOptionLabel = (c) =>
  c?.cityName ?? c?.CityName ?? c?.title ?? c?.Title ?? c?.label ?? c?.Label ?? "";
const cityOptionProvinceId = (c) =>
  Number(c?.provinceId ?? c?.ProvinceId ?? 0) || 0;

const SolarentOperatorAllRequestsPage = ({ hubMode = false } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = useCheckPermission("edit");
  const currentUserId = getCurrentUserId();
  const [reloadToken, setReloadToken] = useState(0);
  const [loanStatusFilter, setLoanStatusFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [locationMaps, setLocationMaps] = useState(null);
  const [caseDrawer, setCaseDrawer] = useState({ open: false, id: null, row: null });
  const [assignmentBusyId, setAssignmentBusyId] = useState(null);

  const listParams = useMemo(() => {
    const parsed = parseAllRequestDrillDownParams(searchParams);
    if (!searchParams.get("pageSize")) {
      return { ...parsed, pageSize: OPERATOR_PAGE_SIZE };
    }
    return parsed;
  }, [searchParams]);

  const listParamsRef = useRef(listParams);
  listParamsRef.current = listParams;

  const {
    rows: dataList,
    totalCount,
    pageMeta,
    loading,
    error,
    showAssignmentUi,
    filterOptions: assignmentFilterOptions,
  } = useManagementAllRequestPagedList(
    LIST_API,
    listParams,
    listParams.assignmentFilter || "all",
    reloadToken,
    {
      assignmentSource: "SolarentOperator",
      filterOptions: ASSIGNMENT_FILTER_OPTIONS_OPERATOR,
    }
  );

  const syncSearchParams = useCallback(
    (nextParams) => {
      const sp = drillDownParamsToSearchParams(nextParams);
      if (hubMode) {
        sp.set("view", "all");
      }
      setSearchParams(sp, { replace: true });
    },
    [hubMode, setSearchParams]
  );

  useEffect(() => {
    setSearchInput(listParams.searchTerm || "");
  }, [listParams.searchTerm]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = (searchInput || "").trim();
      const current = listParamsRef.current;
      if (trimmed === (current.searchTerm || "").trim()) return;
      syncSearchParams(
        withPageReset({
          ...current,
          searchTerm: trimmed,
        })
      );
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput, syncSearchParams]);

  useEffect(() => {
    let cancelled = false;
    CustomerRequestLocationService.loadCustomerRequestLocationLists()
      .then(({ provinces: provinceList, cities: cityList }) => {
        if (cancelled) return;
        const nextProvinces = Array.isArray(provinceList) ? provinceList : [];
        const nextCities = Array.isArray(cityList) ? cityList : [];
        setProvinces(nextProvinces);
        setCities(nextCities);
        setLocationMaps(buildLocationLookupMaps(nextProvinces, nextCities));
      })
      .catch(() => {
        if (cancelled) return;
        setProvinces([]);
        setCities([]);
        setLocationMaps(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCities = useMemo(() => {
    if (!listParams.provinceId) return [];
    return cities.filter(
      (c) => cityOptionProvinceId(c) === Number(listParams.provinceId)
    );
  }, [cities, listParams.provinceId]);

  const reloadList = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  const handlePageChange = useCallback(
    (pageIndex) => {
      syncSearchParams({
        ...listParams,
        pageNumber: pageIndex + 1,
      });
    },
    [listParams, syncSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (pageSize) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          pageSize,
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleSortChange = useCallback(
    (columnId, order) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          sortBy: columnIdToSortBy(columnId),
          sortDirection: order,
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleAssignmentFilterChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          assignmentFilter: value,
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleStatusFilterChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          requestStatus: value || "",
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleProvinceChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          provinceId: value || "",
          cityId: "",
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleCityChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          cityId: value || "",
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handlePartnerChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          partnerReferralLevel1PartnerId: value || "",
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const rows = useMemo(
    () =>
      enrichAllRequestRows(
        enrichAssignmentRows(dataList),
        currentUserId,
        locationMaps
      ),
    [dataList, currentUserId, locationMaps]
  );

  // فقط فیلتر وضعیت وام سمت کلاینت روی صفحهٔ جاری (API فیلتر معادل ندارد)
  const displayedRows = useMemo(
    () =>
      filterAllRequestsRows(rows, {
        loanStatusFilter,
      }),
    [rows, loanStatusFilter]
  );

  const headCells = useMemo(() => {
    const cols = [
      { id: "referenceID", label: "کد درخواست" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "mobile", label: "موبایل" },
      { id: "provinceName", label: "استان" },
      { id: "cityName", label: "شهرستان" },
      { id: "partnerReferralDisplayName", label: "معرف (سطح یک)" },
      { id: "requestConfirmTitle", label: "وضعیت درخواست" },
      { id: "stepTitle", label: "مرحله فعلی" },
    ];
    if (showAssignmentUi) cols.push(assignmentOwnerColumn);
    cols.push(
      {
        id: "leadPriorityTitle",
        label: "اولویت",
        type: "chip",
        model: { colorField: "leadPriorityColor" },
      },
      {
        id: "followUpReminderTitle",
        label: "وضعیت پیگیری",
        type: "chip",
        model: { colorField: "followUpReminderColor" },
      },
      {
        id: "opsMehrsanLabel",
        label: "مهرسان",
        type: "chip",
        model: { colorField: "opsMehrsanColor" },
      },
      {
        id: "opsLendingLabel",
        label: "وام",
        type: "chip",
        model: { colorField: "opsLendingColor" },
      },
      { id: "updateOn", label: "آخرین بروزرسانی", type: "date" }
    );
    return cols;
  }, [showAssignmentUi]);

  const serverPagination = useMemo(
    () => ({
      totalCount,
      page: Math.max(0, (listParams.pageNumber || 1) - 1),
      rowsPerPage: listParams.pageSize || OPERATOR_PAGE_SIZE,
      orderBy: sortByToColumnId(listParams.sortBy),
      order: listParams.sortDirection === "asc" ? "asc" : "desc",
      onPageChange: handlePageChange,
      onRowsPerPageChange: handlePageSizeChange,
      onSortChange: handleSortChange,
    }),
    [
      totalCount,
      listParams.pageNumber,
      listParams.pageSize,
      listParams.sortBy,
      listParams.sortDirection,
      handlePageChange,
      handlePageSizeChange,
      handleSortChange,
    ]
  );

  const onOpenCase = useCallback((row) => {
    setCaseDrawer({ open: true, id: row.id, row });
  }, []);

  const onEditClicked = useCallback((row) => {
    const path = buildCheckNewRequestModifyPath(row, "solarentOperator");
    if (path) openCheckNewRequestModifyPath(path);
  }, []);

  const onOpenWorkspace = useCallback((row) => {
    const path = buildProjectWorkspacePath(row, "solarentOperator", {
      listView: WORKSPACE_LIST_VIEWS.all,
    });
    if (path) openProjectWorkspacePathInNewTab(path);
  }, []);

  const handleClaim = useCallback(
    async (row) => {
      setAssignmentBusyId(row.id);
      try {
        const res = await RequestAssignmentApi.claim(
          "SolarentOperator",
          Number(row.id)
        );
        if (res?.isSuccess) {
          toast.success(res.message || "درخواست با موفقیت دریافت شد.");
          reloadList();
        } else {
          toast.error(
            res?.message ||
              "این درخواست قبلاً توسط کارشناس دیگری دریافت شده است."
          );
          reloadList();
        }
      } catch {
        toast.error("خطا در دریافت درخواست");
      } finally {
        setAssignmentBusyId(null);
      }
    },
    [reloadList]
  );

  const handleRelease = useCallback(
    async (row) => {
      setAssignmentBusyId(row.id);
      try {
        const res = await RequestAssignmentApi.release(
          "SolarentOperator",
          Number(row.id)
        );
        if (res?.isSuccess) {
          toast.success(res.message || "درخواست آزاد شد.");
          reloadList();
        } else {
          toast.error(res?.message || "خطا در آزادسازی درخواست");
        }
      } catch {
        toast.error("خطا در آزادسازی درخواست");
      } finally {
        setAssignmentBusyId(null);
      }
    },
    [reloadList]
  );

  const tableActions = useMemo(() => {
    const actions = [
      {
        icon: <FolderOpenIcon fontSize="small" />,
        onClick: onOpenWorkspace,
        color: "primary",
        tooltip: "پرونده پروژه (Workspace)",
      },
      {
        icon: <ContentPasteSearchIcon fontSize="small" />,
        onClick: onOpenCase,
        color: "info",
        tooltip: "بررسی پرونده",
      },
    ];

    if (canEdit) {
      actions.splice(1, 0, {
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: onEditClicked,
        color: "secondary",
        tooltip: "ویرایش درخواست",
      });
      if (showAssignmentUi) {
        actions.push({
          icon: <PersonAddAlt1Icon fontSize="small" />,
          onClick: handleClaim,
          color: "success",
          tooltip: "دریافت درخواست",
          hidden: (row) =>
            !canShowClaimAction(row, showAssignmentUi) ||
            assignmentBusyId === row.id,
        });
        actions.push({
          icon: <LockOpenIcon fontSize="small" />,
          onClick: handleRelease,
          color: "warning",
          tooltip: "آزادسازی",
          hidden: (row) => !row.canRelease || assignmentBusyId === row.id,
        });
      }
    }

    return actions;
  }, [
    canEdit,
    showAssignmentUi,
    assignmentBusyId,
    onOpenCase,
    handleClaim,
    handleRelease,
    onEditClicked,
    onOpenWorkspace,
  ]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {!hubMode ? <BreadcrumbsComponent /> : null}
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="جستجو و فیلتر سمت سرور — برای پاسخ‌گویی سریع‌تر به مشتری"
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Button
                  component={RouterLink}
                  to="/SLN/SolarentOperator/ApplicantContracts"
                  size="small"
                  variant="outlined"
                  startIcon={<ArticleOutlinedIcon />}
                >
                  قراردادهای متقاضیان
                </Button>
                <IconButton
                  color="primary"
                  onClick={reloadList}
                  aria-label="بروزرسانی لیست"
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            {error ? (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={reloadList}>
                    تلاش مجدد
                  </Button>
                }
              >
                {error}
              </Alert>
            ) : null}

            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="جستجو بر اساس نام، موبایل، کد ملی، شناسه قبض، کد پیگیری..."
                inputProps={{ "data-testid": "operator-workspace-search" }}
                sx={{ flex: "1 1 240px", minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Chip
                size="small"
                variant="outlined"
                label={`${totalCount.toLocaleString("fa-IR")} نتیجه`}
                sx={{ flexShrink: 0 }}
              />

              {showAssignmentUi ? (
                <FormControl size="small" sx={{ flex: "0 0 170px", minWidth: 150 }}>
                  <InputLabel>وضعیت مسئولیت</InputLabel>
                  <Select
                    value={listParams.assignmentFilter || "all"}
                    label="وضعیت مسئولیت"
                    onChange={(e) => handleAssignmentFilterChange(e.target.value)}
                  >
                    {assignmentFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              <Box sx={{ flex: "0 0 190px", minWidth: 170 }}>
                <PartnerReferralFilterSelect
                  value={listParams.partnerReferralLevel1PartnerId || ""}
                  onChange={handlePartnerChange}
                  rows={rows}
                />
              </Box>

              <FormControl size="small" sx={{ flex: "0 0 150px", minWidth: 140 }}>
                <InputLabel>استان</InputLabel>
                <Select
                  value={listParams.provinceId || ""}
                  label="استان"
                  onChange={(e) => handleProvinceChange(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {provinces.map((p) => {
                    const id = provinceOptionId(p);
                    return (
                      <MenuItem key={String(id)} value={id}>
                        {provinceOptionLabel(p)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ flex: "0 0 150px", minWidth: 140 }}
                disabled={!listParams.provinceId}
              >
                <InputLabel>شهر</InputLabel>
                <Select
                  value={listParams.cityId || ""}
                  label="شهر"
                  onChange={(e) => handleCityChange(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {filteredCities.map((c) => {
                    const id = cityOptionId(c);
                    return (
                      <MenuItem key={String(id)} value={id}>
                        {cityOptionLabel(c)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: "0 0 180px", minWidth: 160 }}>
                <InputLabel>وضعیت وام</InputLabel>
                <Select
                  value={loanStatusFilter}
                  label="وضعیت وام"
                  onChange={(e) => setLoanStatusFilter(e.target.value)}
                >
                  {ALL_REQUESTS_LOAN_STATUS_FILTERS.map((opt) => (
                    <MenuItem key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ mr: 0.5 }}
              >
                وضعیت:
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={listParams.requestStatus || ""}
                exclusive
                onChange={(_, val) => handleStatusFilterChange(val ?? "")}
                sx={{ flexWrap: "wrap" }}
              >
                {ALL_REQUESTS_STATUS_FILTERS.map((opt) => (
                  <ToggleButton key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {(listParams.searchTerm ||
                listParams.provinceId ||
                listParams.cityId ||
                listParams.requestStatus ||
                listParams.partnerReferralLevel1PartnerId) && (
                <Button
                  size="small"
                  onClick={() =>
                    syncSearchParams({
                      ...createDefaultDrillDownParams(),
                      assignmentFilter: listParams.assignmentFilter || "all",
                    })
                  }
                >
                  پاک‌کردن فیلترها
                </Button>
              )}
            </Box>

            <EnhancedTable
              rowKey="id"
              rows={displayedRows}
              headCells={headCells}
              defaultSort={{
                orderBy: sortByToColumnId(listParams.sortBy),
                order: listParams.sortDirection === "asc" ? "asc" : "desc",
              }}
              pageSize={listParams.pageSize || OPERATOR_PAGE_SIZE}
              showFilter={false}
              loading={loading}
              tableMinWidth="auto"
              actionsMode="menu"
              actions={tableActions}
              serverPagination={serverPagination}
            />

            {!loading && totalCount > 0 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                صفحه {pageMeta.pageNumber.toLocaleString("fa-IR")} از{" "}
                {Math.max(pageMeta.totalPages, 1).toLocaleString("fa-IR")}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Grid>

      <AllRequestsCaseDrawer
        open={caseDrawer.open}
        requestId={caseDrawer.id}
        listRow={caseDrawer.row}
        onClose={() => setCaseDrawer({ open: false, id: null, row: null })}
        listView={WORKSPACE_LIST_VIEWS.all}
      />
    </Grid>
  );
};

export default SolarentOperatorAllRequestsPage;
